import { useEffect } from 'react';
import { fetchCurrentStaff, refreshSession } from './api';
import { readStoredRefreshToken, useSession } from './session';

/**
 * Khôi phục phiên lúc mở app.
 *
 * Access token chỉ nằm trong bộ nhớ nên tải lại trang là mất. Dùng refresh token
 * trong localStorage lấy access token mới, rồi gọi `/auth/staff/me` để đọc lại
 * vai trò và chi nhánh — hai thứ đó có thể đã đổi kể từ lần đăng nhập trước,
 * nên KHÔNG lấy từ token cũ.
 */
export function useRestoreSession(): void {
  const signIn = useSession((state) => state.signIn);
  const signOut = useSession((state) => state.signOut);
  const finishRestoring = useSession((state) => state.finishRestoring);

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      const refreshToken = readStoredRefreshToken();

      if (refreshToken === null) {
        finishRestoring();
        return;
      }

      try {
        const renewed = await refreshSession(refreshToken);

        // Đặt phiên tạm với thông tin tối thiểu để lời gọi /me kế tiếp có token.
        // `mustChangePassword: false` ở đây là giả định lạc quan; nếu tài khoản còn
        // nợ đổi mật khẩu thì chính /me sẽ trả 403 và ta đăng xuất bên dưới.
        signIn(
          {
            staffId: '',
            name: '',
            role: 'super_admin',
            branchId: null,
            accessToken: renewed.accessToken,
            mustChangePassword: false,
          },
          renewed.refreshToken,
        );

        const me = await fetchCurrentStaff();
        if (cancelled) return;

        signIn(
          {
            staffId: me.staffId,
            name: me.name,
            role: me.role,
            branchId: me.branchId,
            accessToken: renewed.accessToken,
            mustChangePassword: false,
          },
          renewed.refreshToken,
        );
      } catch {
        // Refresh token hết hạn, bị thu hồi, hoặc tài khoản còn nợ đổi mật khẩu —
        // mọi trường hợp đều đưa về màn đăng nhập, không cố đoán
        if (!cancelled) signOut();
      }
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, [signIn, signOut, finishRestoring]);
}
